import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | error
  const attempts = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setState("success");
          await refresh?.();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status) || attempts.current > 12) {
          setState("error");
          return;
        }
      } catch { if (attempts.current > 12) { setState("error"); return; } }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refresh]);

  return (
    <div className="min-h-screen bg-[#0A0D0C] grid-bg flex items-center justify-center px-6">
      <div className="card-emerald p-10 max-w-md w-full text-center animate-fade-up" data-testid="payment-result">
        {state === "checking" && (<><Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto" /><h1 className="font-display text-2xl font-bold mt-6">Confirming your payment…</h1><p className="text-sm text-muted-foreground mt-2">This only takes a moment.</p></>)}
        {state === "success" && (<><CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto glow-emerald rounded-full" /><h1 className="font-display text-2xl font-bold mt-6">You're subscribed!</h1><p className="text-sm text-muted-foreground mt-2">Your QwikLogs plan is now active. Thank you.</p><Button onClick={() => navigate("/app")} className="mt-6 bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="payment-continue-btn">Go to dashboard</Button></>)}
        {state === "error" && (<><XCircle className="w-14 h-14 text-red-400 mx-auto" /><h1 className="font-display text-2xl font-bold mt-6">Payment not completed</h1><p className="text-sm text-muted-foreground mt-2">No charge was made. You can try again anytime.</p><Link to="/app/billing"><Button className="mt-6" variant="outline">Back to billing</Button></Link></>)}
      </div>
    </div>
  );
}
